import { useState } from 'react';
import { ApartmentOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons';

import { PageHeader } from '../../../components/shared';
import { Badge, Button, Card, CardBody, Empty, Select, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { TrinhSuaQuyTrinh } from '../components/TrinhSuaQuyTrinh';
import { QUYEN } from '../constants';
import { useDanhMucQuyTrinh, useDsQuyTrinh, useDsUngDung } from '../hooks/useCauHinhEkyc';

/** Chọn `'moi'` là đang soạn quy trình mới; chuỗi khác là id quy trình. */
type Chon = string | 'moi' | null;

function KhoiLoi({ loi, onThuLai }: { loi: ApiError | null; onThuLai: () => void }) {
  const { t } = useT();
  const dichLoi = useLoi();
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded border border-danger-200 bg-danger-50 px-3 py-2.5">
      <WarningOutlined className="shrink-0 text-danger-600" />
      <span className="min-w-0 text-sm text-danger-700">{t('ekyc.quyTrinh.loiTai', { loi: dichLoi(loi) })}</span>
      <Button variant="outline" size="sm" onClick={onThuLai}>{t('chung.thuLai')}</Button>
    </div>
  );
}

/**
 * Quy trình định danh (`/ekyc/quy-trinh`, phân hệ `EKYC_WORKFLOW`).
 *
 * Dựng theo màn v3 (ảnh người dùng cung cấp 24/10/2026): chọn ứng dụng ở đầu màn, danh sách quy trình bên trái,
 * trình sửa bên phải. Ba chỗ cố ý khác v3:
 *
 * 1. **Không kéo thả khối.** v3 tự ghi "vị trí khối chỉ để nhìn" — thao tác không đổi được gì thì bỏ.
 * 2. **Mã tự sinh từ tên** và chỉ đọc (ADR-UI-0014), thay ô "Mã" gõ tay của v3.
 * 3. **Mọi luật ở máy chủ.** Danh mục bước, lựa chọn của chính sách, lỗi và cảnh báo đều đọc từ API
 *    (`/quy-trinh/danh-muc`, `/quy-trinh/xem-truoc`) — giao diện không giữ bản luật thứ hai.
 */
export function QuyTrinhView() {
  const { t } = useT();
  const dieuHuong = useDieuHuong();
  const quyen = useModulePermissions(QUYEN);
  const tieuDe = t('ekyc.quyTrinh.tieuDe');

  const dsUngDung = useDsUngDung();
  const danhMuc = useDanhMucQuyTrinh();
  const [ungDungChon, setUngDungChon] = useState<string | null>(null);
  const ungDungId = ungDungChon ?? dsUngDung.data?.items[0]?.id ?? null;
  const dsQuyTrinh = useDsQuyTrinh(ungDungId);
  const [chon, setChon] = useState<Chon>(null);
  const [lanTai, setLanTai] = useState(0);

  const items = dsQuyTrinh.data?.items ?? [];
  const idChon = chon === 'moi' ? 'moi' : items.find((q) => q.id === chon)?.id ?? items[0]?.id ?? null;
  const dangSua = idChon === 'moi' ? null : items.find((q) => q.id === idChon) ?? null;

  const taiLai = () => {
    void dsQuyTrinh.refetch().then(() => setLanTai((n) => n + 1));
  };

  let than: React.ReactNode;
  if (dsUngDung.isLoading || danhMuc.isLoading) {
    than = <Skeleton className="h-96 w-full" />;
  } else if (dsUngDung.isError || danhMuc.isError) {
    than = (
      <KhoiLoi
        loi={(dsUngDung.error ?? danhMuc.error) as ApiError | null}
        onThuLai={() => { void dsUngDung.refetch(); void danhMuc.refetch(); }}
      />
    );
  } else if (!ungDungId || !danhMuc.data) {
    than = (
      <Card>
        <Empty
          icon={ApartmentOutlined}
          title={t('ekyc.quyTrinh.chuaCoUngDung')}
          description={t('ekyc.quyTrinh.chuaCoUngDungMo')}
          action={quyen.ungDungDoc ? (
            <Button variant="primary" size="md" onClick={() => dieuHuong('/ekyc/ung-dung')}>
              {t('ekyc.quyTrinh.moUngDung')}
            </Button>
          ) : undefined}
        />
      </Card>
    );
  } else {
    than = (
      <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <nav aria-label={t('ekyc.quyTrinh.danhSach')} className="space-y-2">
          {dsQuyTrinh.isLoading ? (
            [0, 1].map((i) => <Skeleton key={i} className="h-16 w-full" />)
          ) : dsQuyTrinh.isError ? (
            <KhoiLoi loi={dsQuyTrinh.error as ApiError | null} onThuLai={() => void dsQuyTrinh.refetch()} />
          ) : items.length === 0 && chon !== 'moi' ? (
            <p className="rounded-lg border border-dashed border-default p-4 text-sm text-content-secondary">
              {t('ekyc.quyTrinh.rongMo')}
            </p>
          ) : (
            <ul className="space-y-2">
              {items.map((q) => {
                const coLoi = q.vanDe.some((v) => v.muc === 'loi');
                const dangChon = q.id === idChon;
                return (
                  <li key={q.id}>
                    <button
                      type="button"
                      aria-current={dangChon ? 'true' : undefined}
                      onClick={() => setChon(q.id)}
                      className={`w-full rounded-lg border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                        dangChon ? 'border-primary-500 bg-primary-50' : 'border-default bg-surface hover:bg-subtle'
                      }`}
                    >
                      <span className="flex min-w-0 items-center justify-between gap-2">
                        <span className="min-w-0 truncate text-sm font-medium text-content-primary">{q.ten}</span>
                        {q.macDinh && <Badge variant="primary">{t('ekyc.quyTrinh.macDinh')}</Badge>}
                      </span>
                      <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5">
                        <span className="truncate font-mono text-2xs text-content-muted">{q.ma}</span>
                        {!q.dangBat && <Badge variant="neutral">{t('ekyc.quyTrinh.dangTat')}</Badge>}
                        {coLoi && <Badge variant="danger">{t('ekyc.quyTrinh.khongHopLe')}</Badge>}
                      </span>
                    </button>
                  </li>
                );
              })}
              {chon === 'moi' && (
                <li className="rounded-lg border border-primary-500 bg-primary-50 px-3 py-2.5 text-sm font-medium text-content-primary">
                  {t('ekyc.quyTrinh.quyTrinhMoi')}
                </li>
              )}
            </ul>
          )}
        </nav>

        <Card>
          <CardBody>
            {idChon === null ? (
              <Empty
                icon={ApartmentOutlined}
                title={t('ekyc.quyTrinh.rong')}
                description={t('ekyc.quyTrinh.rongMo')}
                action={quyen.quyTrinhTao ? (
                  <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setChon('moi')}>
                    {t('ekyc.quyTrinh.taoQuyTrinh')}
                  </Button>
                ) : undefined}
              />
            ) : (
              <TrinhSuaQuyTrinh
                key={`${ungDungId}:${idChon}:${lanTai}`}
                ungDungId={ungDungId}
                quyTrinh={dangSua}
                danhMuc={danhMuc.data}
                onDaTao={(q) => setChon(q.id)}
                onTaiLai={taiLai}
                onDaXoa={() => setChon(null)}
              />
            )}
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.dinhDanhEkyc') }, { label: tieuDe }]}
          titleIcon={<ApartmentOutlined className="text-primary-600" />}
          title={tieuDe}
          subtitle={t('ekyc.quyTrinh.moTaMan')}
          actions={
            <>
              {(dsUngDung.data?.items.length ?? 0) > 0 && (
                <Select<string>
                  value={ungDungId ?? undefined}
                  onChange={(v) => { setUngDungChon(v); setChon(null); }}
                  aria-label={t('ekyc.quyTrinh.chonUngDung')}
                  className="w-64"
                  options={dsUngDung.data!.items.map((u) => ({ value: u.id, label: u.ten }))}
                />
              )}
              {quyen.quyTrinhTao && ungDungId && danhMuc.data && (
                <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setChon('moi')}>
                  {t('ekyc.quyTrinh.taoQuyTrinh')}
                </Button>
              )}
            </>
          }
        />
        {than}
      </div>
    </div>
  );
}
